import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import { RootState } from '@/src/reducers/types';

import { useDashboardMembers } from './api/getDashboardMembers';
import { useGetLookerReports } from './hooks/useGetLookerReports';

import { ManageUserView } from './components/ManageUserView/ManageUserView';
import SettingsCard from '../../../Elements/SettingsCard/SettingsCard';
import { ReportUrlList } from './components/ReportUrl/ReportUrlList';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import InsertLink from './components/InsertLink/InsertLink';
import { useCreateDashboardReport } from './hooks';
import { showToast } from '@/src/globalFunction';

const Dashboard = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const dashboardMembersQuery = useDashboardMembers({
    projectId: +projectId,
    config: {
      enabled: !!projectId,
    },
  });

  const {
    lookerReports: lookerReportsFromQuery,
    queryInfo: lookerReportsQueryInfo,
    members,
  } = useGetLookerReports(projectId, {
    config: { enabled: !!projectId },
  });
  const [lookerReports, setLookerReports] = useState(lookerReportsFromQuery || []);

  // Sync local state with backend/refetch
  useEffect(() => {
    setLookerReports(lookerReportsFromQuery || []);
  }, [lookerReportsFromQuery]);

  const updateGlobalState = (newUrls) => {
    setLookerReports(newUrls);
    dispatch({
      type: 'SET_PROJECT_SELECTED',
      Selected: {
        lookerReportsUrls: newUrls,
      },
    });
  };

  const [showAddModal, setShowAddModal] = useState(false);

  // Callback to update project store after mutation
  const handleReportsChanged = (latestReports) => {
    updateGlobalState(latestReports);
  };

  const { createDashboardReportAsync } = useCreateDashboardReport(projectId);
  const handleAdd = async (data) => {
    if (data) {
      try {
        await createDashboardReportAsync({
          name: data.name?.trim() || '',
          url: data.url?.trim() || '',
        });
        const result = await lookerReportsQueryInfo.refetch();
        const reports =
          result?.data?.data?.['looker-reports'] || result?.data?.['looker-reports'] || [];
        handleReportsChanged(reports);
        // Push the newly created lookerReports to Selected.lookerReports in project store
        if (reports && reports.length > 0) {
          dispatch({
            type: 'SET_PROJECT_SELECTED',
            Selected: {
              lookerReports: reports,
            },
          });
          showToast('success', 'Report successfully added.');
        }
      } catch (error) {
        showToast('error', `There was a problem adding this report. `);
      }
    }
    setShowAddModal(false);
  };

  if (dashboardMembersQuery.isLoading || lookerReportsQueryInfo.isLoading) {
    return <SectionLoader />;
  }

  const reportsEnabled = lookerReports && lookerReports.length > 0;

  return (
    <SettingsCard
      id="reports"
      title="Reports"
      isReports
      actionButton={
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="tw-ml-8 tw-p-1 tw-rounded-full tw-bg-cfo-blue-50 hover:tw-bg-cfo-blue-100 tw-text-cfo-blue-600 tw-flex tw-items-center "
          title="Add report"
        >
          <Icon
            icon={ICONS.add}
            className="tw-rounded-full tw-border-solid tw-border-2 tw-border-cfo-blue-600 tw-text-[14px]"
          />
          <span className="tw-ml-2">New Report</span>
        </button>
      }
    >
      <form
        id="reports-form"
        className={clsx(
          'tw-flex tw-flex-col tw-gap-[20px]',
          'tw-max-w-full tw-w-full ',
          'tw-py-[20px] tw-px-[30px]'
        )}
      >
        <div className="tw-flex tw-flex-col tw-gap-[10px]">
          <ReportUrlList
            reportUrls={lookerReports || []}
            setReportUrls={updateGlobalState}
            refetchReports={async () => {
              const result = await lookerReportsQueryInfo.refetch();
              const reports =
                result?.data?.data?.['looker-reports'] || result?.data?.['looker-reports'] || [];
              handleReportsChanged(reports);
              return reports;
            }}
          />
          {showAddModal && (
            <InsertLink
              showModal={showAddModal}
              toggleShowModal={setShowAddModal}
              defaultValue={''}
              onSave={handleAdd}
            />
          )}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[20px]">
          <span className="tw-font-brandonTextMedium tw-text-cfo-black tw-font-[450]">
            Manage User View
          </span>

          <ManageUserView
            projectId={+projectId}
            members={members || []}
            reportsEnabled={reportsEnabled}
            onMemberVisibilityChanged={async () => {
              await lookerReportsQueryInfo.refetch();
            }}
          />
        </div>
      </form>
    </SettingsCard>
  );
};

export default Dashboard;
